/**
 * Order screen additions for Location Service Engine tenants: the delivery plan (mode, date, slot, route), the
 * delivery code to give the rider, rural milestones, live ETA while the rider is on the way, substitutions waiting
 * for the customer's answer, refunds, quick reorder and "report a problem". Renders nothing for any other tenant
 * (it doesn't even call the API).
 *
 * ``onLiveTick`` — called on every live refresh so the page's existing map re-reads the rider's position.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, CheckCircle2, Circle, KeyRound, LifeBuoy, Loader2, MapPin, Navigation, RefreshCcw, RotateCcw, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inr, useLse } from "../../lib/lse";

const ACTIVE = new Set(["PLACED", "CONFIRMED", "PREPARING", "READY", "PICKED_UP", "OUT_FOR_DELIVERY"]);
const REFUND_TONE = { REFUNDED: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300", FAILED: "text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300" };
const PAY_LABEL = { PENDING: "Payment pending", SUCCESSFUL: "Paid", FAILED: "Payment failed", REFUND_INITIATED: "Refund initiated", PARTIALLY_REFUNDED: "Partly refunded", REFUNDED: "Refunded" };

function Card({ title, icon: Icon, children, testid }) {
    return (
        <section data-testid={testid} className="bg-card rounded-2xl border border-border p-4">
            <h3 className="font-bold text-sm flex items-center gap-1.5 mb-2">{Icon && <Icon size={15} className="text-primary" />} {title}</h3>
            {children}
        </section>
    );
}

export default function LseOrderExtras({ orderId, status, riderStatus, onLiveTick, className = "" }) {
    const { on, has } = useLse();
    const nav = useNavigate();
    const [x, setX] = useState(null);
    const [live, setLive] = useState(null);
    const [busy, setBusy] = useState(null);
    const tick = useRef(onLiveTick);
    tick.current = onLiveTick;

    const load = useCallback(() => {
        if (!on || !orderId) return;
        api.get(`/lse/orders/${orderId}`).then(({ data }) => setX(data)).catch(() => setX(null));
    }, [on, orderId]);
    useEffect(() => { load(); }, [load, status, riderStatus]);

    const tracking = !!x?.live_tracking && ACTIVE.has(status) && ["ASSIGNED", "PICKED_UP", "OUT_FOR_DELIVERY"].includes(riderStatus);
    useEffect(() => {
        if (!tracking) { setLive(null); return undefined; }
        let alive = true;
        const poll = () => api.get(`/lse/orders/${orderId}/live`).then(({ data }) => {
            if (!alive) return;
            setLive(data);
            tick.current?.();
        }).catch(() => {});
        poll();
        const h = setInterval(poll, 15000);
        return () => { alive = false; clearInterval(h); };
    }, [tracking, orderId]);

    if (!on || !x) return null;
    const plan = x.delivery_plan;
    const pending = (x.substitutions || []).filter((s) => s.substitute?.status === "PENDING");

    const answer = async (line, approve) => {
        setBusy(`sub-${line}`);
        try {
            await api.post(`/lse/orders/${orderId}/substitutions/${line}`, { approve });
            toast.success(approve ? "Substitute approved" : "Item removed — any difference will be refunded");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save your answer"); } finally { setBusy(null); }
    };
    const reorder = async (replace = false) => {
        setBusy("reorder");
        try {
            const { data } = await api.post(`/lse/orders/${orderId}/reorder`, { replace });
            window.dispatchEvent(new Event("cart:update"));
            toast.success(`${data.added} item${data.added === 1 ? "" : "s"} added to your cart${data.skipped?.length ? ` · ${data.skipped.length} unavailable` : ""}`);
            nav("/cart");
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d?.code === "DIFFERENT_RESTAURANT") {
                if (window.confirm(d.message)) { setBusy(null); reorder(true); return; }
            } else toast.error(typeof d === "string" ? d : "Couldn't reorder");
        } finally { setBusy(null); }
    };

    return (
        <div className={`space-y-3 ${className}`} data-testid="lse-order-extras">
            {plan && (
                <Card title="Delivery" icon={Truck} testid="lse-order-plan">
                    <p className="text-sm font-semibold">
                        {plan.label}{plan.date_label ? ` · ${plan.date_label}` : ""}{plan.slot?.label ? ` · ${plan.slot.label}` : ""}
                    </p>
                    {plan.eta_text && plan.mode !== "SCHEDULED" && <p className="text-xs text-muted-foreground mt-0.5">{plan.eta_text}</p>}
                    {x.route?.village && (
                        <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1"><MapPin size={12} /> {x.route.village}{x.route.route ? ` · ${x.route.route} route` : ""}</p>
                    )}
                </Card>
            )}

            {x.delivery_proof?.otp && (
                <Card title="Delivery code" icon={KeyRound} testid="lse-delivery-otp">
                    <p className="text-xs text-muted-foreground">Share this code with the delivery partner when your order arrives.</p>
                    <p className="mt-2 text-3xl font-bold tracking-[0.4em] tabular-nums" aria-label={`Delivery code ${x.delivery_proof.otp.split("").join(" ")}`}>{x.delivery_proof.otp}</p>
                </Card>
            )}

            {tracking && (
                <Card title="Live tracking" icon={Navigation} testid="lse-live">
                    {live?.rider ? (
                        <div className="flex items-end justify-between gap-3">
                            <div>
                                <p className="text-2xl font-bold tabular-nums">{live.eta_minutes != null ? `${live.eta_minutes} min` : "—"}</p>
                                <p className="text-xs text-muted-foreground">
                                    {live.distance_km != null ? `${live.distance_km} km away` : "On the way"}
                                    {live.stop ? ` · stop ${live.stop.position} of ${live.stop.of} on the route` : ""}
                                </p>
                            </div>
                            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><RefreshCcw size={11} /> updates every 15 s</span>
                        </div>
                    ) : <p className="text-xs text-muted-foreground">Waiting for the delivery partner's location…</p>}
                </Card>
            )}

            {x.milestones && (
                <Card title="Progress" icon={CalendarDays} testid="lse-milestones">
                    <ol className="space-y-1.5">
                        {x.milestones.map((m) => (
                            <li key={m.key} className="flex items-center gap-2 text-sm">
                                {m.done ? <CheckCircle2 size={16} className="text-emerald-600 shrink-0" /> : <Circle size={16} className="text-muted-foreground shrink-0" />}
                                <span className={m.done ? "font-semibold" : "text-muted-foreground"}>{m.label}</span>
                            </li>
                        ))}
                    </ol>
                </Card>
            )}

            {pending.length > 0 && (
                <Card title="Your answer needed" icon={RotateCcw} testid="lse-substitutions">
                    {pending.map((s) => (
                        <div key={s.line_no} className="py-2 first:pt-0 border-b last:border-0 border-border">
                            <p className="text-sm"><span className="font-semibold">{s.name}</span> is out of stock.</p>
                            <p className="text-xs text-muted-foreground">We can send <span className="font-semibold text-foreground">{s.substitute.name}</span> ({inr(s.substitute.unit_price)} each) instead. You won't pay more.</p>
                            <div className="mt-2 flex gap-2">
                                <button type="button" data-testid={`lse-sub-approve-${s.line_no}`} disabled={!!busy} onClick={() => answer(s.line_no, true)}
                                        className="flex-1 min-h-[40px] rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">Yes, send it</button>
                                <button type="button" data-testid={`lse-sub-reject-${s.line_no}`} disabled={!!busy} onClick={() => answer(s.line_no, false)}
                                        className="flex-1 min-h-[40px] rounded-xl border border-border text-sm font-semibold disabled:opacity-50">No, remove it</button>
                            </div>
                        </div>
                    ))}
                </Card>
            )}

            {(x.refunds || []).length > 0 && (
                <Card title={`Refunds · ${PAY_LABEL[x.payment_display_status] || x.payment_display_status}`} icon={RotateCcw} testid="lse-refunds">
                    <ul className="space-y-1.5">
                        {x.refunds.map((r) => (
                            <li key={r.id} className="flex items-center justify-between gap-2 text-sm">
                                <span className="min-w-0 truncate">{inr(r.amount)} · {r.method === "WALLET" ? "to your wallet" : "to your payment method"}</span>
                                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${REFUND_TONE[r.status] || "bg-muted text-muted-foreground"}`}>{r.status.replace(/_/g, " ").toLowerCase()}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            <div className="flex gap-2 flex-wrap">
                {x.can_reorder && (
                    <button type="button" data-testid="lse-reorder" onClick={() => reorder(false)} disabled={busy === "reorder"}
                            className="min-h-[44px] flex-1 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy === "reorder" ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />} Order again
                    </button>
                )}
                {has("customer_support") && (
                    <Link to={`/support?order=${encodeURIComponent(orderId)}`} data-testid="lse-report-problem"
                          className="min-h-[44px] flex-1 rounded-xl border border-border text-sm font-semibold inline-flex items-center justify-center gap-2">
                        <LifeBuoy size={16} /> Report a problem
                    </Link>
                )}
            </div>
        </div>
    );
}
